import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import {
  Box,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Divider,
  Typography,
} from '@material-ui/core'
import { Alert, AlertTitle } from '@material-ui/lab'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { Controller, useForm } from 'react-hook-form'
import moment from 'moment'

import {
  BaseDialog,
  Label,
  LoadingFeedback,
  PendingBillingEmails,
  Select as SelectComponent,
} from 'components'
import BillingEmails from 'views/PaymentServiceTemplates/components/PaymentServiceTemplateForm/components/BillingEmails'

import { useFetch, useSnackbar } from 'hooks'
import constants from 'constants/index'
import helpers from 'helpers'
import * as service from 'service'
import schema from './schema'
import useStyles from './styles'

const NO_TEMPLATE_ID = ''

const PaymentServiceDialog = ({
  open,
  setOpen,
  paymentContractId,
  paymentService,
  refresh = () => {},
  refreshContract = () => {},
}) => {
  const isEdit = !!paymentService
  const snackbar = useSnackbar()
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [emails, setEmails] = useState([])

  const isPlatformType = paymentService?.isPlatformType
  const isRequestTemplate =
    !paymentService?.originPaymentServiceId && open && !isPlatformType

  const { response: templatesResponse } = useFetch(
    service.dponet.paymentServiceTemplates.get,
    { active: true, perPage: 1000 },
    [],
    isRequestTemplate,
  )

  const templates = templatesResponse?.data?.paymentServices || []

  const { handleSubmit, control, errors, setValue, watch } = useForm({
    validationSchema: schema,
    defaultValues: {
      originPaymentServiceId: NO_TEMPLATE_ID,
      name: paymentService?.name || '',
      description: paymentService?.description || '',
      active: paymentService ? paymentService?.active : true,
      price: helpers.formatters.currencyInput(paymentService?.price) || '',
      endDate: paymentService?.endDate ? moment(paymentService.endDate) : null,
      renewable: paymentService?.renewable ?? false,
      enableAdvance: paymentService ? paymentService?.enableAdvance : true,
      valueAdvance: paymentService?.valueAdvance || '',
      periodAdvance: paymentService?.periodAdvance || 'day',
      frequencyAdvance: paymentService?.frequencyAdvance || 'weekly',
    },
  })

  const originPaymentServiceId = watch('originPaymentServiceId')
  const renewable = watch('renewable')

  const selectedTemplate = templates.find(
    (template) => template?.id === originPaymentServiceId,
  )
  const isGovernanceReview =
    !!paymentService?.isGovernanceReviewType ||
    !!selectedTemplate?.isGovernanceReviewType
  // o template é fixo no item do catálogo; aqui só é exibido
  const governanceProjectTemplate = isEdit
    ? paymentService?.governanceProjectTemplate
    : selectedTemplate?.governanceProjectTemplate

  useEffect(() => {
    if (!renewable) return

    setValue('enableAdvance', false)
  }, [renewable, setValue])

  useEffect(() => {
    if (
      (isEdit && paymentService?.originPaymentService?.id) ||
      !originPaymentServiceId ||
      originPaymentServiceId === NO_TEMPLATE_ID
    ) {
      return
    }

    const template = templates.find(
      (templateOption) => templateOption?.id === originPaymentServiceId,
    )

    if (!template) return

    setValue('name', template.name)
    setValue('description', template.description || '')
    setValue('price', helpers.formatters.currencyInput(template.price))
    setValue('endDate', template.endDate ? moment(template.endDate) : null)
    setValue('renewable', template.renewable)
    setValue('enableAdvance', template.enableAdvance)
    setValue('valueAdvance', template.valueAdvance || '')
    setValue('periodAdvance', template.periodAdvance || 'day')
    setValue('frequencyAdvance', template.frequencyAdvance || 'weekly')
    setEmails(
      (template.billingEmails || []).map((billingEmail) => billingEmail.email),
    )
    // eslint-disable-next-line
  }, [originPaymentServiceId])

  const onSubmit = async (data) => {
    setLoading(true)

    const payload = {
      ...data,
      price: helpers.formatters.currencyOutput(data.price),
      endDate: data.endDate ? moment(data.endDate).format('YYYY-MM-DD') : null,
    }

    if (!payload.originPaymentServiceId) delete payload.originPaymentServiceId

    try {
      if (isEdit) {
        await service.dponet.paymentContractServices.update({
          paymentContractId,
          paymentServiceId: paymentService?.id,
          ...payload,
        })
      } else {
        const response = await service.dponet.paymentContractServices.create({
          paymentContractId,
          ...payload,
        })
        const newPaymentServiceId = response?.data?.paymentService?.id

        await Promise.all(
          emails.map((email) =>
            service.dponet.paymentServiceTemplates.createBillingEmail({
              paymentServiceTemplateId: newPaymentServiceId,
              email,
            }),
          ),
        )
      }

      snackbar.open({
        message: `Serviço ${isEdit ? 'atualizado' : 'criado'} com sucesso!`,
        variant: 'success',
      })
      refresh()
      refreshContract()
      setOpen(false)
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          `Falha ao ${isEdit ? 'atualizar' : 'criar'} o serviço.`,
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <BaseDialog
      open={open}
      setOpen={setOpen}
      title={isEdit ? 'Editar serviço' : 'Cadastrar serviço'}
      closeButtonText="Cancelar"
      actionButtonText="Salvar"
      disabledSubmit={
        loading || (isGovernanceReview && !governanceProjectTemplate)
      }
      formRef="payment-service-form"
      dialogSize="md"
    >
      <LoadingFeedback open={loading} />
      <form id="payment-service-form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Alert severity="warning">
              <AlertTitle>Atenção</AlertTitle>
              <Typography variant="body2">
                {isPlatformType
                  ? `
                  Neste serviço só é possível alterar o nome, a descrição e o valor. O
                  índice de reajuste será aplicado na renovação do contrato caso
                  o índice de renovação seja diferente de "sem reajuste".`
                  : `Mesmo com a renovação automática ativa, não será aplicado
                  índice de reajuste neste serviço. Caso este contrato tenha
                  data de término vencida, ao chegar a data de renovação do
                  contrato, o serviço será renovado para a mesma data de
                  renovação utilizada pelo contrato.`}
              </Typography>
            </Alert>
          </Grid>
          {!isPlatformType && (
            <>
              {isEdit && paymentService?.originPaymentService?.id ? (
                <Grid item xs={12}>
                  <TextField
                    label="Serviço de origem"
                    fullWidth
                    variant="outlined"
                    disabled
                    defaultValue={paymentService?.originPaymentService?.name}
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Controller
                    as={
                      <SelectComponent
                        label="Template (opcional)"
                        items={[
                          {
                            id: NO_TEMPLATE_ID,
                            name: 'Nenhum (criar sem template)',
                          },
                          ...templates,
                        ]}
                      />
                    }
                    name="originPaymentServiceId"
                    control={control}
                  />
                </Grid>
              )}
            </>
          )}
          {isGovernanceReview && (
            <Grid item xs={12}>
              {governanceProjectTemplate ? (
                <TextField
                  label="Projeto de governança (template)"
                  fullWidth
                  variant="outlined"
                  disabled
                  value={governanceProjectTemplate.name}
                  helperText="Definido no serviço do catálogo."
                />
              ) : (
                <Alert severity="warning">
                  Este serviço do catálogo ainda não tem template de projeto de
                  governança configurado. Configure no catálogo antes de usar.
                  {isEdit &&
                    ' Até lá não é possível salvar alterações neste serviço. Para desativá-lo, use a opção "Inativar" na lista de serviços do contrato.'}
                </Alert>
              )}
            </Grid>
          )}
          <Grid item xs={12}>
            <Controller
              as={
                <TextField
                  label="Nome"
                  fullWidth
                  variant="outlined"
                  error={!!errors.name}
                  helperText={errors?.name?.message}
                />
              }
              name="name"
              control={control}
              mode="onBlur"
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              as={
                <TextField
                  label="Descrição (opcional)"
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  error={!!errors.description}
                  helperText={errors?.description?.message}
                />
              }
              name="description"
              control={control}
              mode="onBlur"
            />
          </Grid>
          <Grid item xs={12} md={isPlatformType ? 12 : 6}>
            <Controller
              as={
                <TextField
                  label="Valor"
                  fullWidth
                  variant="outlined"
                  error={!!errors.price}
                  helperText={errors?.price?.message}
                  InputProps={{ startAdornment: <Box mr={0.5}>R$</Box> }}
                />
              }
              name="price"
              control={control}
              onChange={([event]) =>
                helpers.formatters.currencyInput(event.target.value)
              }
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            className={clsx({ [classes.hidden]: isPlatformType })}
          >
            <Controller
              as={
                <KeyboardDatePicker
                  label="Término"
                  format="DD/MM/yyyy"
                  variant="dialog"
                  clearable
                  fullWidth
                  inputVariant="outlined"
                  error={!!errors.endDate}
                  helperText={
                    errors?.endDate?.message ||
                    "Obrigatório quando 'Notificar vencimento' estiver ativo. Deixe em branco para serviços sem término."
                  }
                />
              }
              name="endDate"
              control={control}
              onChange={([date]) => date}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Controller
                  as={<Switch color="primary" />}
                  name="active"
                  control={control}
                  onChange={([event]) => event.target.checked}
                  disabled={isPlatformType}
                />
              }
              label="Ativo"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Controller
                  as={<Switch color="primary" />}
                  name="renewable"
                  control={control}
                  onChange={([event]) => event.target.checked}
                  disabled={isPlatformType}
                />
              }
              label="Renovação automática"
            />
          </Grid>
          <Grid
            item
            xs={12}
            className={clsx({ [classes.hidden]: isPlatformType })}
          >
            <Divider />
          </Grid>
          <Grid
            item
            xs={12}
            className={clsx({ [classes.hidden]: isPlatformType })}
          >
            <FormControlLabel
              control={
                <Controller
                  disabled={renewable}
                  as={<Switch color="primary" />}
                  name="enableAdvance"
                  control={control}
                  onChange={([event]) => event.target.checked}
                />
              }
              label="Notificar vencimento"
            />
          </Grid>
          <Grid
            item
            xs={12}
            className={clsx({ [classes.hidden]: isPlatformType })}
          >
            <Label title="Antecedência de aviso" marginTopChildren={1}>
              <Grid container spacing={2}>
                <Grid item md={4} xs={12}>
                  <Controller
                    as={
                      <TextField
                        label="Antecedência"
                        type="number"
                        fullWidth
                        variant="outlined"
                        error={!!errors.valueAdvance}
                        helperText={errors?.valueAdvance?.message}
                      />
                    }
                    name="valueAdvance"
                    control={control}
                    mode="onBlur"
                    type="number"
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  <Controller
                    as={
                      <SelectComponent
                        label="Período"
                        items={constants.paymentServiceTemplate.PERIOD_OPTIONS}
                        error={!!errors.periodAdvance}
                        helperText={errors?.periodAdvance?.message}
                      />
                    }
                    name="periodAdvance"
                    control={control}
                    mode="onBlur"
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  <Controller
                    as={
                      <SelectComponent
                        label="Frequência dos disparos"
                        items={
                          constants.paymentServiceTemplate.FREQUENCY_OPTIONS
                        }
                        error={!!errors.frequencyAdvance}
                        helperText={errors?.frequencyAdvance?.message}
                      />
                    }
                    name="frequencyAdvance"
                    control={control}
                    mode="onBlur"
                  />
                </Grid>
              </Grid>
            </Label>
          </Grid>
          <Grid
            item
            xs={12}
            className={clsx({ [classes.hidden]: isPlatformType })}
          >
            {isEdit ? (
              <BillingEmails paymentServiceTemplateId={paymentService?.id} />
            ) : (
              <PendingBillingEmails emails={emails} setEmails={setEmails} />
            )}
          </Grid>
        </Grid>
      </form>
    </BaseDialog>
  )
}

PaymentServiceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  paymentContractId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  paymentService: PropTypes.object,
  refresh: PropTypes.func,
  refreshContract: PropTypes.func,
}

export default PaymentServiceDialog
